import type { BrowserGeolocation } from "./browser";
import { inject, Injectable } from "@angular/core";
import { AlertController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { DeviceService } from "@omni/services/device/device.service";
import { AuthenticationService } from "@omni/services/authentication.service";
import { IoConfigurationFeatureCodes } from "@omni/classes/authentication/user.class";
import { firstValueFrom, of } from "rxjs";
import { AITool } from "@omni/ai/tools/tool.decorator";

const LOCATION_POPUP_DONT_ASK_KEY_PREFIX = 'location_access_popup_dont_ask_';

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  #deviceService = inject(DeviceService);
  #alertController = inject(AlertController);
  #translate = inject(TranslateService);
  #authService = inject(AuthenticationService);

  #geolocationInstance: Promise<BrowserGeolocation> | null = null;
  #instance: BrowserGeolocation | null = null;

  #ensureInitialized() {
    if (!this.#instance) {
      throw new Error('Geolocation instance not initialized. Call init() first.');
    }
  }
  get status$() {
    this.#ensureInitialized();
    return this.#instance?.status$;
  }
  
  get isEnabled$() {
    this.#ensureInitialized();
    return this.#instance?.isEnabled$;
  }

  get currentPosition$() {
    this.#ensureInitialized();
    return this.#instance?.currentPosition$;
  }

  getCurrentPosition() {
    this.#ensureInitialized();
    return this.#instance?.getCurrentPosition();
  }

  getCurrentPositionName() {
    this.#ensureInitialized();
    return this.#instance?.getCurrentPositionName();
  }

  /**
   * Where the rep is, for the assistant: coordinates and the place they reverse-geocode to. Location
   * is permissioned and can be off, and a device can fail to get a fix, so the outcomes stay distinct
   * rather than surfacing as an exception the model has to interpret.
   */
  @AITool(
    'get_current_location',
    "The rep's current location: latitude, longitude and the nearest place name (locality, "
    + "administrative area, country). Use when they ask where they are or what is near them, or when "
    + "a question needs a place they have not named.",
    [],
  )
  async getCurrentLocationForAssistant(): Promise<any> {
    try {
      if (!await firstValueFrom(this.isEnabled$ ?? of(false))) {
        return { success: false, error: 'Location is turned off for this app, so their position is unavailable.' };
      }

      const coords = await this.getCurrentPosition();
      if (!coords?.latitude || !coords?.longitude) {
        return { success: false, error: 'The device could not get a location fix.' };
      }

      // A fix without a name is still worth returning, so a failed lookup does not fail the call.
      const place = await this.getCurrentPositionName()?.catch(() => null);
      return {
        success: true,
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy ?? null,
        locality: place?.locality ?? null,
        administrativeArea: place?.administrativeArea ?? null,
        countryName: place?.countryName ?? null,
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get the current location' };
    }
  }

  requestPermission() {
    this.#ensureInitialized();
    return this.#instance?.requestPermission();
  }

  openLocationSettings() {
    this.#ensureInitialized();
    return this.#instance?.openLocationSettings();
  }

  async init(): Promise<BrowserGeolocation> {
    if (!this.#geolocationInstance) {
      this.#geolocationInstance = this.#createGeolocationInstance();
    } else {
      this.#instance ??= await this.#geolocationInstance;
      return this.#instance;
    }
    this.#instance = await this.#geolocationInstance;
    const status = await firstValueFrom(this.status$);
    console.log('Geolocation permission status:', status);
    if (status === 'prompt') {
      await this.#instance?.requestPermission();
    } else if (status === 'denied' && this.#deviceService.isNativeApp) {
      console.warn('Geolocation permission denied. Prompting user to open settings.');
      if (!this.#shouldSkipLocationPopup()) {
        const canShowDontAskAgain = !this.#authService.hasIoConfigWithFeatureCode(
          IoConfigurationFeatureCodes.BU_GEOLOCATION_GA_ALWAYSSHOWLOCATIONPERMISSIONPOPUP
        );
        const result = await this.askPermissionForLocation(canShowDontAskAgain);
        if (result.role === 'confirm') {
          console.log('User chose to open location settings.');
          await this.#instance?.openLocationSettings();
        } else if (result.role === 'dont-ask-again') {
          this.#saveDontAskAgainPreference();
        }
      }
    } else if (status === 'granted') {
      console.log('Geolocation permission is already granted.');
    }
    return this.#instance;
  }

  async #createGeolocationInstance(): Promise<BrowserGeolocation> {
    if (this.#deviceService.isElectron()) {
      return await import('./electron').then(m => new m.ElectronGeolocation());
    } else if (this.#deviceService.isAndroid()) {
      return await import('./android').then(m => new m.AndroidGeolocation());
    } else if (this.#deviceService.isIOS()) {
      return await import('./ios').then(m => new m.IOSGeolocation());
    }
    // Fallback to browser implementation for web
    return await import('./browser').then(m => new m.BrowserGeolocation());
  }

  async askPermissionForLocation(showDontAskAgain = false): Promise<{ role?: string }> {
    const buttons: any[] = [
      { text: this.#translate.instant('SETTINGS'), role: 'confirm' },
      { text: this.#translate.instant('CANCEL'), role: 'cancel' },
    ];
    if (showDontAskAgain) {
      buttons.push({
        text: this.#translate.instant('LOCATION_PERMISSION_DONT_ASK_AGAIN'),
        role: 'dont-ask-again',
      });
    }
    const alert = await this.#alertController.create({
      header: this.#translate.instant('USER_DENIED_PERMISSION'),
      message: this.#translate.instant('USER_LOCATION_DENIED'),
      buttons,
      cssClass: 'location-permission-alert',
    });
    await alert.present();
    return await alert.onDidDismiss();
  }

  #dontAskKey(): string {
    const userId = this.#authService.user?.systemUserID ?? 'unknown';
    return `${LOCATION_POPUP_DONT_ASK_KEY_PREFIX}${userId}`;
  }

  #shouldSkipLocationPopup(): boolean {
    return localStorage.getItem(this.#dontAskKey()) === 'true';
  }

  #saveDontAskAgainPreference(): void {
    localStorage.setItem(this.#dontAskKey(), 'true');
  }
}